import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Edit, Eye, Loader2, Mail, Package, Plus, RefreshCw, Search, Shield, ShieldAlert, Trash2, Users, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { formatDate } from "@/lib/billing";
import { adminUserAction, fetchManagedUsers, getInitials, isCurrentUserSuperAdmin, isCurrentUserTeamManager, type AdminUserRow } from "@/lib/adminUsers";
import { useAuth } from "@/hooks/useAuth";

export default function TeamsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [packageFilter, setPackageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [removeTarget, setRemoveTarget] = useState<AdminUserRow | null>(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState<AdminUserRow | null>(null);
  const [forceDeletePhrase, setForceDeletePhrase] = useState("");
  const { data: canManage, isLoading: adminLoading } = useQuery({
    queryKey: ["can-manage-team", user?.id],
    queryFn: () => isCurrentUserTeamManager(user?.id),
    enabled: !!user,
  });
  const { data: isSuperAdmin = false, isLoading: superAdminLoading } = useQuery({
    queryKey: ["is-super-admin", user?.id],
    queryFn: () => isCurrentUserSuperAdmin(user?.id),
    enabled: !!user,
  });
  const { data: managed, isLoading, error } = useQuery({
    queryKey: ["admin-users-managed"],
    queryFn: fetchManagedUsers,
    enabled: !!canManage,
  });
  const users = useMemo(() => managed?.users ?? [], [managed?.users]);
  const companies = useMemo(() => managed?.companies ?? [], [managed?.companies]);
  const canManageAll = Boolean(managed?.can_manage_all);
  const teamUsage = managed?.team_usage ?? [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const next = users.filter((item) => [
      item.full_name,
      item.email,
      item.phone_number,
      item.company_name,
      item.company_role,
      item.country,
      item.account_status,
      canManageAll ? item.package_name : "",
      item.subscription_status,
      item.roles.join(" "),
    ].join(" ").toLowerCase().includes(q))
      .filter((item) => roleFilter === "all" || item.roles.includes(roleFilter) || item.company_role === roleFilter)
      .filter((item) => companyFilter === "all" || item.company_id === companyFilter)
      .filter((item) => !canManageAll || packageFilter === "all" || (packageFilter === "none" ? !item.package_id : item.package_id === packageFilter))
      .filter((item) => statusFilter === "all" || item.account_status === statusFilter);

    return next.sort((a, b) => {
      const value = (item: AdminUserRow) => {
        if (sortKey === "email") return item.email ?? "";
        if (sortKey === "role") return item.roles[0] ?? item.company_role ?? "";
        if (sortKey === "company") return item.company_name ?? "";
        if (sortKey === "package") return item.package_name ?? "";
        if (sortKey === "status") return item.account_status ?? "";
        if (sortKey === "created") return item.created_at ?? "";
        if (sortKey === "last_login") return item.last_login_at ?? "";
        return item.full_name ?? "";
      };
      return String(value(a)).localeCompare(String(value(b)));
    });
  }, [users, search, roleFilter, companyFilter, packageFilter, statusFilter, sortKey, canManageAll]);

  const packages = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((item) => {
      if (item.package_id && item.package_name) map.set(item.package_id, item.package_name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [users]);

  const roles = useMemo(() => {
    const set = new Set<string>();
    users.forEach((item) => {
      item.roles.forEach((role) => set.add(role));
      if (item.company_role) set.add(item.company_role);
    });
    return Array.from(set).sort();
  }, [users]);

  const removalImpact = useQuery({
    queryKey: ["team-user-removal-impact", removeTarget?.id],
    queryFn: () => adminUserAction("analyze_user_removal", { user_id: removeTarget!.id }),
    enabled: !!removeTarget,
  });
  const forceDeleteImpact = useQuery({
    queryKey: ["team-user-force-delete-impact", forceDeleteTarget?.id],
    queryFn: () => adminUserAction("analyze_user_removal", { user_id: forceDeleteTarget!.id }),
    enabled: !!forceDeleteTarget,
  });

  const removeUser = useMutation({
    mutationFn: ({ userId, companyId }: { userId: string; companyId?: string | null }) => adminUserAction("remove_user", {
      action: "remove_user",
      user_id: userId,
      company_id: companyId,
      deletion_mode: "remove_from_company",
      reason: "Removed from company/team from /teams",
    }),
    onSuccess: async (result: any) => {
      const summary = result?.summary;
      toast.success(summary
        ? `User removed from company/team. Removed ${summary.deleted_records_count ?? 0} membership records.`
        : "User removed from company/team");
      setRemoveTarget(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not remove user"),
  });

  const forceDeleteUser = useMutation({
    mutationFn: ({ userId, confirmation }: { userId: string; confirmation: string }) => adminUserAction("force_delete_user", {
      action: "force_delete_user",
      user_id: userId,
      deletion_mode: "force",
      confirmation,
      reason: "Force deleted from /teams",
    }),
    onSuccess: async (result: any) => {
      const summary = result?.summary;
      const deleted = summary?.deleted_records_count ?? 0;
      const preserved = summary?.preserved_records_count ?? 0;
      toast.success(summary?.auth_account_deleted
        ? `User permanently deleted. Deleted ${deleted} records; detached ${summary?.detached_records_count ?? 0} references.`
        : `Full delete did not complete. Deleted ${deleted} records; preserved ${preserved} records.`);
      setForceDeleteTarget(null);
      setForceDeletePhrase("");
      await queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not completely remove user"),
  });

  const resendInvitation = useMutation({
    mutationFn: (invitationId: string) => adminUserAction("resend_invitation", { invitation_id: invitationId }),
    onSuccess: async (result: any) => {
      toast.success(result?.message ?? "Invitation resent");
      await queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not resend invitation"),
  });

  const cancelInvitation = useMutation({
    mutationFn: (invitationId: string) => adminUserAction("cancel_invitation", { invitation_id: invitationId }),
    onSuccess: async (result: any) => {
      toast.success(result?.message ?? "Invitation cancelled");
      await queryClient.invalidateQueries({ queryKey: ["admin-users-managed"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not cancel invitation"),
  });

  const confirmSafeRemove = () => {
    const userId = removeTarget?.id;
    if (!userId) {
      toast.error("Select a user before removing access.");
      return;
    }
    removeUser.mutate({ userId, companyId: removeTarget?.company_id });
  };

  const confirmForceDelete = () => {
    const userId = forceDeleteTarget?.id;
    if (!userId) {
      toast.error("Select a user before complete removal.");
      return;
    }
    const confirmation = forceDeletePhrase.trim();
    if (confirmation !== "DELETE") {
      toast.error("Type DELETE to confirm complete removal.");
      return;
    }
    forceDeleteUser.mutate({ userId, confirmation });
  };

  const exportCsv = () => {
    const rows = filtered.map((item) => ({
      id: item.id,
      name: item.full_name ?? "",
      email: item.email ?? "",
      phone: item.phone_number ?? "",
      company: item.company_name ?? "",
      country: item.country ?? "",
      status: item.account_status,
      verified: item.email_verified,
      roles: item.roles.join("|"),
      package: item.package_name ?? "",
      subscription: item.subscription_status ?? "",
      websites: item.websites_count,
      articles: item.articles_count,
      media: item.media_count,
      invoices: item.invoice_count,
    }));
    const csv = [Object.keys(rows[0] ?? {}).join(","), ...rows.map((row) => Object.values(row).map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "delapresse-users.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (adminLoading || superAdminLoading || isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (!canManage) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-center px-4">
        <Shield className="h-12 w-12 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">Access restricted</h2>
        <p className="max-w-sm text-sm text-muted-foreground">Only platform admins or company admins can manage Delapresse users.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">User Administration</h1>
          <p className="text-xs text-muted-foreground">{managed?.can_manage_all ? "Manage all companies." : "Manage users in your company."} Create, invite, edit, remove, and audit users.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}><Download className="mr-2 h-4 w-4" />Export</Button>
          <Button asChild size="sm"><Link to="/team/users/create"><Plus className="mr-2 h-4 w-4" />Create user</Link></Button>
        </div>
      </header>

      <main className="space-y-6 p-6">
        {!canManageAll && teamUsage.length > 0 && (
          <div className="rounded-md border border-border bg-card p-4 text-sm">
            {teamUsage.map((usage) => (
              <div key={usage.company_id} className="flex items-center justify-between gap-3">
                <span className="font-medium">{usage.company_name}</span>
                <span className="text-muted-foreground">{usage.used} / {usage.limit ?? "unlimited"} team members used</span>
              </div>
            ))}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-4">
          <Stat label="Users" value={users.length} icon={Users} />
          <Stat label="Active" value={users.filter((item) => item.account_status === "active" || item.account_status === "trialing").length} icon={Shield} />
          <Stat label="Pending invites" value={users.filter((item) => item.account_status === "invited" || item.invitation_status === "pending").length} icon={Mail} />
          <Stat label={canManageAll ? "Paid packages" : "Team limit"} value={canManageAll ? users.filter((item) => item.subscription_status === "active").length : (teamUsage[0]?.limit ?? 0)} icon={Package} />
        </div>

        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">All users</CardTitle>
            <div className="grid gap-3 lg:grid-cols-[minmax(240px,1fr)_180px_180px_180px_180px_180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search name, email, phone, role, company, package, status..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {["active", "invited", "suspended", "disabled"].map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roles.map((role) => <SelectItem key={role} value={role}>{role.replaceAll("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger><SelectValue placeholder="Company" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All companies</SelectItem>
                  {companies.map((company) => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {canManageAll ? (
                <Select value={packageFilter} onValueChange={setPackageFilter}>
                  <SelectTrigger><SelectValue placeholder="Package" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All packages</SelectItem>
                    <SelectItem value="none">No package</SelectItem>
                    {packages.map((pkg) => <SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <div />}
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger><SelectValue placeholder="Sort" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Sort by name</SelectItem>
                  <SelectItem value="email">Sort by email</SelectItem>
                  <SelectItem value="role">Sort by role</SelectItem>
                  <SelectItem value="company">Sort by company</SelectItem>
                  {canManageAll && <SelectItem value="package">Sort by package</SelectItem>}
                  <SelectItem value="status">Sort by status</SelectItem>
                  <SelectItem value="created">Sort by created</SelectItem>
                  <SelectItem value="last_login">Sort by last login</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                Could not load users: {(error as Error).message}
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                {search ? "No users match this filter." : "No users are available from the current Delapresse profile data."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Roles</TableHead>
                    {canManageAll && <TableHead>Package</TableHead>}
                    <TableHead>Usage</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={item.avatar_url ?? undefined} />
                            <AvatarFallback>{getInitials(item.full_name)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="truncate font-medium">{item.full_name ?? "Unnamed user"}</div>
                            <div className="truncate text-xs text-muted-foreground">{item.email ?? "Email unavailable"}</div>
                            <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                              <span className="truncate">{item.company_name ?? "No company"}</span>
                              {item.company_role ? <span>- {item.company_role === "viewer" ? "member" : item.company_role}</span> : null}
                              {!item.company_id && canManageAll ? <Badge variant="outline">Orphaned</Badge> : null}
                            </div>
                            {item.invitation_status === "pending" && <div className="truncate text-xs text-muted-foreground">Invite expires {formatDate(item.invitation_expires_at)}</div>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="space-y-1">
                        <StatusBadge status={item.account_status} />
                        <div><Badge variant={item.email_verified ? "default" : "secondary"}>{item.email_verified ? "Verified" : item.invitation_status === "pending" ? "Invitation pending" : "Pending"}</Badge></div>
                      </TableCell>
                      <TableCell>
                        <div className="flex max-w-[220px] flex-wrap gap-1">
                          {item.roles.length ? item.roles.map((role) => <Badge key={role} variant="outline">{role.replaceAll("_", " ")}</Badge>) : <span className="text-xs text-muted-foreground">No roles</span>}
                        </div>
                      </TableCell>
                      {canManageAll && (
                        <TableCell>
                          <div className="font-medium">{item.package_name ?? "No package"}</div>
                          <div className="mt-1"><StatusBadge status={item.subscription_status ?? "none"} /></div>
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-muted-foreground">
                        {item.websites_count} sites · {item.articles_count} articles · {item.media_count} media
                      </TableCell>
                      <TableCell>{formatDate(item.last_login_at)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          {item.is_invitation_pending ? (
                            <>
                              <Button variant="outline" size="icon" title="Resend invitation" disabled={!item.invitation_id || resendInvitation.isPending} onClick={() => item.invitation_id && resendInvitation.mutate(item.invitation_id)}><RefreshCw className="h-4 w-4" /></Button>
                              <Button variant="outline" size="icon" title="Cancel invitation" disabled={!item.invitation_id || cancelInvitation.isPending} onClick={() => item.invitation_id && cancelInvitation.mutate(item.invitation_id)}><XCircle className="h-4 w-4" /></Button>
                            </>
                          ) : (
                            <>
                              <Button asChild variant="outline" size="icon" title="View user"><Link to={`/team/users/${item.id}`}><Eye className="h-4 w-4" /></Link></Button>
                              <Button asChild variant="outline" size="icon" title="Edit user"><Link to={`/team/users/${item.id}/edit`}><Edit className="h-4 w-4" /></Link></Button>
                              <Button
                                variant="outline"
                                size="icon"
                                title={item.company_id ? "Remove from company/team" : "No company membership to remove"}
                                disabled={!item.company_id}
                                onClick={() => setRemoveTarget(item)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {!item.is_invitation_pending && (
                            <Button
                              variant="destructive"
                              size="icon"
                              title={isSuperAdmin ? "Completely remove user" : "Only Super Admins can completely remove users"}
                              disabled={!isSuperAdmin}
                              onClick={() => {
                                setForceDeletePhrase("");
                                setForceDeleteTarget(item);
                              }}
                            >
                              <ShieldAlert className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => {
        if (!open && !removeUser.isPending) setRemoveTarget(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from company/team?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes only the selected company/team membership and related open invitations. The Supabase Auth account and profile are kept, so the same email can be re-added and linked again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border border-border p-3 text-sm">
            <div className="font-medium">{removeTarget?.full_name ?? removeTarget?.email ?? "Selected user"}</div>
            <div className="mt-2 grid gap-1 text-muted-foreground">
              {removalImpact.isLoading ? (
                <span>Checking related data...</span>
              ) : (
                <>
                  <span>Company memberships: {removalImpact.data?.impact?.company_memberships?.length ?? 0}</span>
                  <span>Roles: {removalImpact.data?.impact?.roles?.length ?? 0}</span>
                  <span>Invitations: {removalImpact.data?.impact?.invitations?.length ?? 0}</span>
                  <span>Subscriptions: {removalImpact.data?.impact?.subscriptions?.length ?? 0}</span>
                  <span>Invoices kept: {removalImpact.data?.impact?.invoices?.length ?? 0}</span>
                  <span>Websites kept: {removalImpact.data?.impact?.websites?.length ?? 0}</span>
                  <span>Articles kept: {removalImpact.data?.impact?.articles?.length ?? 0}</span>
                  <span>Activity logs kept: {removalImpact.data?.impact?.activity_count ?? 0}</span>
                </>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSafeRemove} disabled={!removeTarget?.id || removeUser.isPending || removalImpact.isLoading}>
              {removeUser.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Remove from company/team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!forceDeleteTarget} onOpenChange={(open) => {
        if (!open && !forceDeleteUser.isPending) {
          setForceDeleteTarget(null);
          setForceDeletePhrase("");
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user completely?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the user from the entire platform and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              This deletes the Supabase Auth account, profile, team memberships, roles, invitations, statuses, cached email references, and user-owned mappings. Type DELETE to unlock the final button.
            </div>
            {!isSuperAdmin && (
              <div className="flex items-start gap-2 rounded-md border border-border p-3 text-sm text-muted-foreground">
                <ShieldAlert className="mt-0.5 h-4 w-4" />
                Only platform Super Admins can completely remove users.
              </div>
            )}
            <div className="rounded-md border border-border p-3 text-sm">
              <div className="font-medium">{forceDeleteTarget?.full_name ?? forceDeleteTarget?.email ?? "Selected user"}</div>
              <div className="mt-2 grid gap-1 text-muted-foreground">
                {forceDeleteImpact.isLoading ? (
                  <span>Checking related data...</span>
                ) : (
                  <>
                    <span>Company memberships removed: {forceDeleteImpact.data?.impact?.company_memberships?.length ?? 0}</span>
                    <span>Roles removed: {forceDeleteImpact.data?.impact?.roles?.length ?? 0}</span>
                    <span>Invitations expired: {forceDeleteImpact.data?.impact?.invitations?.length ?? 0}</span>
                    <span>Subscriptions affected: {forceDeleteImpact.data?.impact?.subscriptions?.length ?? 0}</span>
                    <span>Invoices preserved: {forceDeleteImpact.data?.impact?.invoices?.length ?? 0}</span>
                    <span>Websites preserved: {forceDeleteImpact.data?.impact?.websites?.length ?? 0}</span>
                    <span>Articles preserved: {forceDeleteImpact.data?.impact?.articles?.length ?? 0}</span>
                    <span>Activity logs preserved: {forceDeleteImpact.data?.impact?.activity_count ?? 0}</span>
                  </>
                )}
              </div>
            </div>
            <Input
              value={forceDeletePhrase}
              onChange={(event) => setForceDeletePhrase(event.target.value)}
              placeholder="Type DELETE"
              disabled={!isSuperAdmin || forceDeleteUser.isPending}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={forceDeleteUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmForceDelete}
              disabled={!isSuperAdmin || !forceDeleteTarget?.id || forceDeletePhrase.trim() !== "DELETE" || forceDeleteUser.isPending || forceDeleteImpact.isLoading}
            >
              {forceDeleteUser.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-2 h-4 w-4" />}
              Delete user completely
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent"><Icon className="h-5 w-5 text-muted-foreground" /></div>
        <div><p className="text-xl font-bold">{value.toLocaleString()}</p><p className="text-xs text-muted-foreground">{label}</p></div>
      </CardContent>
    </Card>
  );
}
